import { useState, useEffect, useCallback } from "react";
import type { User } from "../types/user";
import { fetchUsersApi, updateSubscriptionApi, fetchMessagesApi, type VisitorMessage } from "../api/admin";

export function useAdmin(token: string | null, isSuperadmin: boolean) {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [adminError, setAdminError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [messages, setMessages] = useState<VisitorMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!token || !isSuperadmin) return;
    setIsLoadingUsers(true);
    setAdminError(null);
    try {
      const data = await fetchUsersApi(token);
      setUsers(data);
    } catch (err: any) {
      console.error(err);
      setAdminError(err.message || "Something went wrong fetching the user registry.");
    } finally {
      setIsLoadingUsers(false);
    }
  }, [token, isSuperadmin]);

  const handleUpdateSubscription = useCallback(async (userId: number, currentStatus: string) => {
    if (!token) return;
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    setUpdatingUserId(userId);
    setAdminError(null);

    try {
      await updateSubscriptionApi(token, userId, nextStatus);
      // Update local state list
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, subscription_status: nextStatus } : u))
      );
    } catch (err: any) {
      console.error(err);
      setAdminError(err.message || "Failed to update subscription status.");
    } finally {
      setUpdatingUserId(null);
    }
  }, [token]);

  const fetchMessages = useCallback(async () => {
    if (!token || !isSuperadmin) return;
    setIsLoadingMessages(true);
    setAdminError(null);
    try {
      const data = await fetchMessagesApi(token);
      setMessages(data);
    } catch (err: any) {
      console.error(err);
      setAdminError(err.message || "Failed to retrieve visitor messages.");
    } finally {
      setIsLoadingMessages(false);
    }
  }, [token, isSuperadmin]);

  useEffect(() => {
    if (isSuperadmin && token) {
      fetchUsers();
      fetchMessages();
    }
  }, [isSuperadmin, token, fetchUsers, fetchMessages]);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return {
    users,
    filteredUsers,
    isLoadingUsers,
    searchQuery,
    setSearchQuery,
    adminError,
    setAdminError,
    updatingUserId,
    fetchUsers,
    handleUpdateSubscription,
    messages,
    isLoadingMessages,
    fetchMessages,
  };
}
